import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthShell } from "@/components/AuthShell";
import { tenantsApi } from "@/api/endpoints/tenants";
import { authStorage } from "@/lib/auth-storage";
import { useSession } from "@/contexts/SessionContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

export function AcceptInvitationPage(): JSX.Element {
  const params = useParams({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!params.token) throw new Error("missing-token");
      const accept = await tenantsApi.acceptInvitation(params.token);
      const switched = await tenantsApi.switch({ tenant_id: accept.tenant_id });
      authStorage.set(switched.access_token);
      return accept.tenant_id;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setDone(true);
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(translateError(err.code, err.message));
      else setError(translateError("COMMON_UNKNOWN"));
    },
  });

  useEffect(() => {
    if (!isLoading && isAuthenticated && !done && !acceptMutation.isPending) {
      acceptMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  if (!isLoading && !isAuthenticated) {
    return (
      <AuthShell sideTitle="You have an invitation.">
        <div>
          <h1>Sign in to accept</h1>
          <p>Please sign in or create an account, then we will return you here.</p>
        </div>
        <Link
          to="/login"
          search={{ redirect: `/invitations/${params.token}/accept` }}
          className="btn btn-primary btn-lg btn-block"
        >
          Sign in to continue
        </Link>
        <Link to="/register" className="btn btn-outline btn-lg btn-block">
          Create account
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell sideTitle="Welcome aboard.">
        <div>
          <h1>Invitation accepted</h1>
          <p>You are now a member of the workspace.</p>
        </div>
        <button
          className="btn btn-primary btn-lg btn-block"
          onClick={() => navigate({ to: "/dashboard" })}
          data-testid="accept-go"
        >
          Open workspace
        </button>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell sideTitle="Invitation problem.">
        <div>
          <h1>We could not accept this invitation</h1>
          <p>{error}</p>
        </div>
        <Link to="/dashboard" className="btn btn-outline btn-lg btn-block">
          Back to workspace
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell sideTitle="Accepting invitation…">
      <p>Hold on while we add you to the workspace.</p>
    </AuthShell>
  );
}
